import { useRef, useState } from "react";
import { api, formatTRY, toNum } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileUp, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const PdfImport = ({ onDone }) => {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const [rows, setRows] = useState(null);

  const pick = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/retail/import-pdf", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 180000 });
      if (!r.data.rows.length) return toast.error("PDF'de ürün bulunamadı");
      setRows(r.data.rows.map((x) => ({ ...x, on: true })));
    } catch (e) { toast.error(e.response?.data?.detail || "PDF okunamadı"); }
    finally { setBusy(false); }
  };

  const set = (i, k, v) => setRows(rows.map((x, j) => j === i ? { ...x, [k]: v } : x));
  const apply = async () => {
    const sel = rows.filter((x) => x.on).map((x) => ({ name: x.name, group: x.group || "", unit: x.unit, package_grams: toNum(x.package_grams), package_price: toNum(x.package_price), retail_price: toNum(x.retail_price), package_vp: toNum(x.package_vp), match_id: x.match_id }));
    if (!sel.length) return toast.error("Seçili ürün yok");
    setBusy(true);
    try {
      const r = await api.post("/retail/import-apply", sel);
      toast.success(`${r.data.created} yeni ürün eklendi, ${r.data.updated} ürün güncellendi`);
      setRows(null); onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setBusy(false); }
  };

  const nCreate = rows?.filter((x) => x.on && !x.match_id).length || 0;
  const nUpdate = rows?.filter((x) => x.on && x.match_id).length || 0;
  return (
    <>
      <input ref={ref} type="file" accept="application/pdf" className="hidden" onChange={pick} data-testid="retail-pdf-input" />
      <Button variant="outline" disabled={busy} onClick={() => ref.current?.click()} data-testid="retail-pdf-btn">
        {busy && !rows ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <FileUp className="mr-2 h-4 w-4" />}{busy && !rows ? "PDF okunuyor…" : "Fiyat Listesi PDF Yükle"}
      </Button>
      <Dialog open={!!rows} onOpenChange={(o) => !o && setRows(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-4xl" data-testid="pdf-import-dialog">
          <DialogHeader><DialogTitle>PDF'den Ürün ve Fiyat Güncelleme</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">{rows?.length} ürün bulundu · <b className="text-primary">{nCreate} yeni</b> eklenecek, <b>{nUpdate}</b> mevcut ürün güncellenecek. PDF'deki <b>Önerilen Müşteri Fiyatı</b> otomatik olarak paket satış fiyatı olur; her yüklemede güncellenir. Değerleri düzenleyebilir, istemediklerinizin işaretini kaldırabilirsiniz. Stok miktarları değişmez.</p>
          <div className="overflow-x-auto"><div className="min-w-[680px]">
          <div className="grid grid-cols-12 gap-2 px-1 text-[11px] uppercase tracking-wide text-muted-foreground"><span className="col-span-1" /><span className="col-span-4">Ürün</span><span className="col-span-2 text-right">Paket</span><span className="col-span-2 text-right">Alış (%50 SV) ₺</span><span className="col-span-2 text-right">Müşteri Fiyatı ₺</span><span className="col-span-1 text-right">VP</span></div>
          <div className="space-y-1.5">
            {rows?.map((r, i) => (
              <div key={i}>
              {(i === 0 || rows[i - 1].group !== r.group) && <p className="mt-2 border-b border-border pb-1 text-xs font-bold uppercase tracking-wide text-primary" data-testid={`pdf-group-${i}`}>{r.group || "Diğer"}</p>}
              <div className={`grid grid-cols-12 items-center gap-2 rounded-md px-1 py-1 ${r.on ? "" : "opacity-40"}`} data-testid={`pdf-row-${i}`}>
                <Checkbox className="col-span-1" checked={r.on} onCheckedChange={(v) => set(i, "on", !!v)} data-testid={`pdf-row-check-${i}`} />
                <div className="col-span-4 min-w-0">
                  <Input className="h-8 text-sm" value={r.name} onChange={(e) => set(i, "name", e.target.value)} data-testid={`pdf-row-name-${i}`} />
                  <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{r.match_id ? <>Güncelle: {r.match_name}{r.old && r.old.retail_price !== toNum(r.retail_price) ? ` (${formatTRY(r.old.retail_price)} → ${formatTRY(toNum(r.retail_price))})` : ""}</> : <span className="text-primary">Yeni ürün</span>}</p>
                </div>
                <Input className="col-span-2 h-8 text-right text-sm" inputMode="decimal" value={r.package_grams} onChange={(e) => set(i, "package_grams", e.target.value)} data-testid={`pdf-row-grams-${i}`} />
                <Input className="col-span-2 h-8 text-right text-sm" inputMode="decimal" value={r.package_price} onChange={(e) => set(i, "package_price", e.target.value)} data-testid={`pdf-row-cost-${i}`} />
                <Input className="col-span-2 h-8 text-right text-sm" inputMode="decimal" value={r.retail_price} onChange={(e) => set(i, "retail_price", e.target.value)} data-testid={`pdf-row-retail-${i}`} />
                <Input className="col-span-1 h-8 text-right text-sm" inputMode="decimal" value={r.package_vp} onChange={(e) => set(i, "package_vp", e.target.value)} data-testid={`pdf-row-vp-${i}`} />
              </div>
              </div>
            ))}
          </div>
          </div></div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRows(null)}>Vazgeç</Button>
            <Button disabled={busy} onClick={apply} data-testid="pdf-import-apply">{busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Depoya Uygula ({nCreate + nUpdate})</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
